import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatMoney } from "@/lib/currency";
import { useAuth } from "@/contexts/AuthContext";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import Modal from "@/components/Modal";
import { Field, SummaryStat } from "@/pages/Income";
import { Plus, Pencil, Trash2, PauseCircle, PlayCircle } from "lucide-react";
import DateField from "@/components/DateField";

const CATS = ["Entertainment","Software","Health","News","Music","Video","Cloud","Other"];
const CYCLES = ["monthly","quarterly","yearly"];

export default function Subscriptions() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ name:"", category:"Entertainment", amount:"", billing_cycle:"monthly", next_bill_date:new Date().toISOString().slice(0,10), is_active:true, last_used_days_ago:"" });

  const load = useCallback(async () => { const { data } = await api.get("/subscriptions"); setItems(data); }, []);
  useEffect(() => { load(); }, [load]);

  const startNew = () => { setEdit(null); setForm({ name:"", category:"Entertainment", amount:"", billing_cycle:"monthly", next_bill_date:new Date().toISOString().slice(0,10), is_active:true, last_used_days_ago:"" }); setOpen(true); };
  const startEdit = (i) => { setEdit(i); setForm({ ...i, last_used_days_ago: i.last_used_days_ago ?? "" }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, amount: parseFloat(form.amount),
      last_used_days_ago: form.last_used_days_ago === "" ? null : parseInt(form.last_used_days_ago) };
    try {
      if (edit) await api.put(`/subscriptions/${edit.id}`, payload); else await api.post("/subscriptions", payload);
      toast.success(t("common.toast_saved")); setOpen(false); load();
    } catch { toast.error(t("common.toast_failed")); }
  };
  const toggle = async (i) => {
    await api.put(`/subscriptions/${i.id}`, { ...i, is_active: !i.is_active });
    toast.success(i.is_active ? "Paused" : "Resumed"); load();
  };
  const remove = async (id) => { if (!confirm("Delete subscription?")) return; await api.delete(`/subscriptions/${id}`); toast.success(t("common.toast_deleted")); load(); };

  const cur = user?.currency || "USD";
  const monthly = (amt, cyc) => cyc === "yearly" ? amt/12 : cyc === "quarterly" ? amt/3 : amt;
  const totalMonthly = items.filter(i => i.is_active).reduce((a,b) => a + monthly(b.amount, b.billing_cycle), 0);
  const totalYearly = totalMonthly * 12;
  const unused = items.filter(i => i.is_active && (i.last_used_days_ago||0) >= 30);

  return (
    <div>
      <PageHeader
        subtitle={t("pages.subscriptions.subtitle")}
        title={t("pages.subscriptions.title")}
        action={<button className="btn-primary" onClick={startNew} data-testid="add-sub-btn"><Plus size={16}/> {t("pages.subscriptions.add")}</button>}
      />

      <div className="grid md:grid-cols-4 gap-5 mb-6 stagger">
        <SummaryStat label={t("pages.subs_body.active")} value={items.filter(i => i.is_active).length}/>
        <SummaryStat label={t("pages.subs_body.monthly_cost")} value={formatMoney(totalMonthly, cur)}/>
        <SummaryStat label={t("pages.subs_body.yearly_cost")} value={formatMoney(totalYearly, cur)}/>
        <SummaryStat label={t("pages.subs_body.unused_30d")} value={unused.length}/>
      </div>

      {items.length === 0 ? (
        <EmptyState title={t("pages.subscriptions.empty_title")} body={t("pages.subs_body.empty_body")}
          action={<button className="btn-primary" onClick={startNew}><Plus size={16}/> {t("pages.subscriptions.add")}</button>}/>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 stagger">
          {items.map(s => {
            const isUnused = (s.last_used_days_ago || 0) >= 30;
            return (
              <div key={s.id} className={`card-flat p-5 ${!s.is_active ? "opacity-60" : ""}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-base font-medium" style={{fontFamily:'var(--font-heading)'}}>{s.name}</div>
                    <div className="text-xs text-[var(--text-muted)] mt-0.5">{s.category}</div>
                  </div>
                  {isUnused && s.is_active && <span className="badge badge-danger">{t("pages.subs_body.unused_days", { count: s.last_used_days_ago })}</span>}
                  {!s.is_active && <span className="badge badge-neutral">{t("pages.subs_body.paused")}</span>}
                </div>
                <div className="mt-4 num-lg">{formatMoney(s.amount, cur)}<span className="text-sm text-[var(--text-muted)] font-normal"> /{s.billing_cycle}</span></div>
                <div className="text-xs text-[var(--text-muted)] mt-1">{t("pages.subs_body.next")}: {s.next_bill_date}</div>
                <div className="mt-4 flex justify-end gap-1">
                  <button className="btn-danger-ghost" onClick={() => toggle(s)} title={s.is_active ? t("pages.subs_body.pause") : t("pages.subs_body.resume")}>
                    {s.is_active ? <PauseCircle size={15}/> : <PlayCircle size={15}/>}
                  </button>
                  <button className="btn-danger-ghost" onClick={() => startEdit(s)}><Pencil size={15}/></button>
                  <button className="btn-danger-ghost" onClick={() => remove(s.id)}><Trash2 size={15}/></button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={edit ? t("pages.subs_body.edit_title") : t("pages.subs_body.add_title")}>
        <form onSubmit={save} className="grid gap-4">
          <Field label={t("pages.subs_body.service_name")}><input required className="field-input" value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="input-sub-name"/></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label={t("common.category")}>
              <select className="field-input" value={form.category} onChange={e => setForm({...form, category: e.target.value})}>
                {CATS.map(c => <option key={c}>{c}</option>)}
              </select>
            </Field>
            <Field label={t("pages.subs_body.amount_l", { cur })}><input type="number" step="0.01" required className="field-input" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} data-testid="input-sub-amount"/></Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label={t("pages.subs_body.billing_cycle")}>
              <select className="field-input" value={form.billing_cycle} onChange={e => setForm({...form, billing_cycle: e.target.value})}>
                {CYCLES.map(c => <option key={c}>{c}</option>)}
              </select>
            </Field>
            <Field label={t("pages.subs_body.next_bill_date")}><DateField value={form.next_bill_date} onChange={v => setForm({...form, next_bill_date: v})} testid="input-sub-date"/></Field>
          </div>
          <Field label={t("pages.subs_body.days_since_last")}><input type="number" className="field-input" value={form.last_used_days_ago} onChange={e => setForm({...form, last_used_days_ago: e.target.value})} placeholder="e.g. 45"/></Field>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.is_active} onChange={e => setForm({...form, is_active: e.target.checked})}/> {t("pages.subs_body.active_checkbox")}
          </label>
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-ghost" onClick={() => setOpen(false)}>{t("common.cancel")}</button>
            <button type="submit" className="btn-primary" data-testid="submit-sub-btn">{edit ? t("common.save") : t("common.add")}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
